import { reactive, markRaw } from 'vue';
import { validateManifest, PLUGIN_STATES } from './plugin-manifest';

export const pluginRegistry = reactive({
  plugins: {},
  readingTypes: {},
  widgets: {},
  themes: {},
  routes: [],
  storeExtensions: {},
  apiExtensions: {},
  wsHandlers: {},
  interfaceActions: {},
});

export class PluginManager {
  constructor(app, router, store, staticStore) {
    this._app = app;
    this._router = router;
    this._store = store;
    this._staticStore = staticStore;
  }

  register(manifest) {
    const validation = validateManifest(manifest);
    if (!validation.valid) {
      console.error(`[PluginManager] Invalid manifest for "${manifest.name}":`, validation.errors);
      return false;
    }

    pluginRegistry.plugins[manifest.name] = {
      manifest,
      state: PLUGIN_STATES.REGISTERED,
    };
    return true;
  }

  activate(pluginName) {
    const entry = pluginRegistry.plugins[pluginName];
    if (!entry) {
      console.error(`[PluginManager] Plugin "${pluginName}" is not registered.`);
      return false;
    }
    if (entry.state === PLUGIN_STATES.ACTIVE) {
      return true;
    }

    const api = this._createSandboxedApi(pluginName);

    try {
      entry.manifest.install(api);
      entry.state = PLUGIN_STATES.ACTIVE;
      return true;
    } catch (err) {
      console.error(`[PluginManager] Error activating "${pluginName}":`, err);
      entry.state = PLUGIN_STATES.ERROR;
      return false;
    }
  }

  deactivate(pluginName) {
    const entry = pluginRegistry.plugins[pluginName];
    if (!entry) return false;

    try {
      if (typeof entry.manifest.uninstall === 'function') {
        entry.manifest.uninstall();
      }
    } catch (err) {
      console.error(`[PluginManager] Error during uninstall of "${pluginName}":`, err);
    }

    this._removeRegistrations(pluginName);
    entry.state = PLUGIN_STATES.INACTIVE;
    return true;
  }

  uninstall(pluginName) {
    this.deactivate(pluginName);
    delete pluginRegistry.plugins[pluginName];
  }

  _createSandboxedApi(pluginName) {
    const manager = this;

    return {
      registerReadingType(key, component, meta = {}) {
        pluginRegistry.readingTypes[key] = {
          component: markRaw(component),
          meta,
          plugin: pluginName,
        };
      },

      registerWidget(type, component, meta = {}) {
        pluginRegistry.widgets[type] = {
          component: markRaw(component),
          meta,
          plugin: pluginName,
        };
      },

      registerTheme(name, cssVars) {
        pluginRegistry.themes[name] = {
          cssVars,
          plugin: pluginName,
        };
      },

      registerRoute(routeConfig) {
        try {
          manager._router.addRoute(routeConfig);
          pluginRegistry.routes.push({ ...routeConfig, plugin: pluginName });
        } catch (err) {
          console.error(`[PluginManager] Failed to add route for "${pluginName}":`, err);
        }
      },

      registerWebSocketHandler(context, reference, handler) {
        if (!pluginRegistry.wsHandlers[context]) {
          pluginRegistry.wsHandlers[context] = {};
        }
        pluginRegistry.wsHandlers[context][reference || '_default'] = {
          handler,
          plugin: pluginName,
        };
      },

      registerInterfaceAction(actionType, handler) {
        pluginRegistry.interfaceActions[actionType] = {
          handler,
          plugin: pluginName,
        };
      },

      extendStore(namespace, defaults = {}) {
        const key = `plugin_${namespace}`;
        if (!manager._store[key]) {
          manager._store[key] = { ...defaults };
        }
        pluginRegistry.storeExtensions[key] = {
          namespace,
          plugin: pluginName,
        };
      },

      registerApiService(namespace, methods) {
        pluginRegistry.apiExtensions[namespace] = {
          methods,
          plugin: pluginName,
        };
      },

      getStore() {
        return Object.freeze({ ...manager._store });
      },

      getStaticStore() {
        return Object.freeze({ ...manager._staticStore });
      },

      registerComponent(name, component) {
        manager._app.component(name, markRaw(component));
      },
    };
  }

  _removeRegistrations(pluginName) {
    for (const [key, val] of Object.entries(pluginRegistry.readingTypes)) {
      if (val.plugin === pluginName) delete pluginRegistry.readingTypes[key];
    }
    for (const [key, val] of Object.entries(pluginRegistry.widgets)) {
      if (val.plugin === pluginName) delete pluginRegistry.widgets[key];
    }
    for (const [key, val] of Object.entries(pluginRegistry.themes)) {
      if (val.plugin === pluginName) delete pluginRegistry.themes[key];
    }
    pluginRegistry.routes = pluginRegistry.routes.filter(r => r.plugin !== pluginName);
    for (const [key, val] of Object.entries(pluginRegistry.storeExtensions)) {
      if (val.plugin === pluginName) {
        delete this._store[key];
        delete pluginRegistry.storeExtensions[key];
      }
    }
    for (const [key, val] of Object.entries(pluginRegistry.apiExtensions)) {
      if (val.plugin === pluginName) delete pluginRegistry.apiExtensions[key];
    }
    for (const [ctx, refs] of Object.entries(pluginRegistry.wsHandlers)) {
      for (const [ref, val] of Object.entries(refs)) {
        if (val.plugin === pluginName) delete pluginRegistry.wsHandlers[ctx][ref];
      }
      if (Object.keys(pluginRegistry.wsHandlers[ctx]).length === 0) {
        delete pluginRegistry.wsHandlers[ctx];
      }
    }
    for (const [key, val] of Object.entries(pluginRegistry.interfaceActions)) {
      if (val.plugin === pluginName) delete pluginRegistry.interfaceActions[key];
    }
  }
}
